import { For, Show, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Heading, Text } from '~ui/text';
import { Button } from '~ui/button';
import { PlusIcon, TrashIcon } from '~ui/icons';
import { NumberBox } from '~ui/field';
import { formatCurrency, formatNumber, todayISO } from '~/lib/format';
import { annualDistribution, name, price } from '~data';
import { FundPicker } from '~/features/fund-picker';
import Cards from '~/features/cards';
import LoanCards from '~/features/loan-cards';
import { Select } from '~ui/select';
import { type AccountKind } from '~/data/portfolios';
import {
    accountNetIncome,
    accountValue,
    accounts,
    activateAccount,
    addPosition,
    canCopyFromMain,
    copyFromMain,
    isEmpty,
    isShown,
    loans,
    removePosition,
    reset,
    setAmount,
    setLoanBalance,
    setLoanRate,
    loanHoldings,
    loanIncome,
    loanInterest,
    loanIntoSheltered,
    movePosition,
    totalDebt,
    totalIncome,
    totalInterest,
    totalValue,
    equity,
    netIncome,
    portfolioYield,
    borrowRate,
    borrowedIncome,
    borrowedYield,
    carryPerMonth,
    deductibleInterest,
    leverage,
    spread,
    type LoanRef,
    type SandboxAccount,
    type SandboxHolding,
} from '~/stores/sandbox-store';

// Scratch paper. Type a fund and an amount into an account and see what it pays.
//
// Deliberately not the Accounts page with a different portfolio behind it. The
// real book is built from transactions, because a record of what happened has
// to be auditable back to the trades that made it. A sandbox has no trades to be
// auditable to — inventing a date and a commission to answer "what would $20,000
// of HMAX pay" is ceremony in the way of the question.
//
// Nothing here is saved. Reloading the page empties it.

const ASOF = todayISO();

/** What one position pays a month, at the published rate. */
const monthlyIncomeFor = (symbol: string, units: number): number | null => {
    const annual = annualDistribution(symbol, ASOF);
    return annual == null ? null : (units * annual) / 12;
};

/**
 * Where a loan-funded position lands before you say otherwise.
 *
 * The first taxable account, because that is the only place borrowing to invest
 * is deductible in Canada — defaulting into a TFSA would quietly produce the one
 * arrangement nobody wants. Falls back to the first account if a portfolio ever
 * has no taxable one.
 */
const defaultAccountFor = (): AccountKind => (accounts.find((a) => !a.sheltered) ?? accounts[0]).kind;

/** Income less interest, for one loan. */
const carry = (loan: LoanRef): number | null => {
    const income = loanIncome(loan);
    return income == null ? null : income - loanInterest(loan);
};

const Cell: Component<{ children: JSX.Element; class?: string }> = (props) => <div class={`flex h-row shrink-0 items-center px-2 ${props.class ?? ''}`}>{props.children}</div>;

/**
 * One position, wherever it is being shown from.
 *
 * The account list and the loan list are the same rows read two ways — by where
 * a holding sits and by what paid for it — so they are one component. The only
 * difference is the account picker, which appears when the row is being read
 * from the loan side, because that is the side where "which account" is the
 * thing you are deciding.
 */
const PositionRow: Component<{ account: SandboxAccount; holding: SandboxHolding; showAccount?: boolean }> = (props) => {
    const unit = () => price(props.holding.symbol);
    const value = () => (unit() == null ? null : props.holding.units * unit()!);
    const loan = () => props.holding.loan;

    return (
        <div class='flex h-row items-center border-b hover:bg-muted'>
            <Cell class='w-16 shrink-0'>
                <Text weight='medium'>{props.holding.symbol}</Text>
            </Cell>

            <Show
                when={props.showAccount}
                fallback={
                    <Cell class='min-w-0 flex-1'>
                        <Text tone='muted' class='truncate'>
                            {name(props.holding.symbol) ?? ''}
                        </Text>
                    </Cell>
                }>
                {/* Which account the borrowed money went into. The control, not a
                    label: it is the decision this row exists to let you make, and
                    it is what decides whether the interest is deductible. */}
                <div class='flex min-w-0 flex-1 items-center'>
                    <Select
                        class='w-36 shrink-0'
                        options={accounts.map((a) => ({ value: a.kind, label: a.label }))}
                        value={props.account.kind}
                        onChange={(e) => movePosition(props.account.kind, props.holding.symbol, loan(), e.currentTarget.value as AccountKind)}
                    />
                </div>
            </Show>

            {/* On the account side, say what paid for it. Cash rows say nothing —
                that is the default and labelling it would be noise. */}
            <Show when={!props.showAccount && loan()}>
                <Cell class='w-28 shrink-0'>
                    {/* The facility's own name, not its kind: two HELOCs at two
                        banks both read "HELOC" and the point of the row is which
                        one paid. */}
                    <Text tone='muted' class='truncate'>
                        {loans.find((l) => l.id === loan())?.label ?? ''}
                    </Text>
                </Cell>
            </Show>

            {/* Two figures, and only two: what you put in, and what it hands
                back each month. Units and unit price were on the row too and
                neither is a thing anyone plans in — they are how the amount is
                stored, not how it is decided. */}
            <NumberBox
                value={value() ?? 0}
                onValue={(n) => setAmount(props.account.kind, props.holding.symbol, n, loan())}
                prefix='$'
                width='w-32 shrink-0'
                border='l'
                disabled={unit() == null}
            />

            <Cell class='w-28 shrink-0 justify-end border-l'>
                <Text tone='green'>{formatCurrency(monthlyIncomeFor(props.holding.symbol, props.holding.units))}</Text>
            </Cell>
            <div class='flex h-full w-7 shrink-0 items-center justify-center border-l'>
                <Button size='fill' icon={<TrashIcon />} title={`Remove ${props.holding.symbol}`} onClick={() => removePosition(props.account.kind, props.holding.symbol, loan())} />
            </div>
        </div>
    );
};

/**
 * One account as a card.
 *
 * Three bands, each doing one job: what the account is and how to add to it,
 * then the positions, then what it comes to. Half the page wide, so the header
 * cannot also carry the totals and the cash box the way it did at full width —
 * cramming them onto one line at this size is how the previous version became
 * unreadable.
 *
 * The body flexes and the footer sits at the bottom, so two cards side by side
 * line their footers up whatever they hold. Without that the totals float at the
 * end of the shorter list and there is nothing to read across.
 */
/**
 * One account as a card: what it is and what it comes to, the holdings, and the
 * way to add one.
 *
 * The heading is set large because at this size the label is the thing you find
 * the card by — four cards in a grid are told apart by their names, and a name
 * in the same 14px as everything else makes you read the card to find out which
 * one it is.
 *
 * Value and net income sit together top right, one under the other, because they
 * are the same measurement at two horizons: what it is worth now and what it
 * hands you monthly. Net rather than gross — a card whose holdings were bought
 * with a loan is not paying you what the distributions say.
 *
 * Adding is at the foot rather than in the header. The header is the answer and
 * the foot is where you go on building; putting the control between them made
 * the header read as part of the form.
 */
const AccountCard: Component<{ account: SandboxAccount }> = (props) => {
    const held = () => props.account.holdings;
    const netPerMonth = () => {
        const net = accountNetIncome(props.account);
        return net == null ? null : net / 12;
    };

    return (
        // min-h so an empty account is still a card. Without it the four boxes
        // size to their contents and an untouched grid is a row of slivers.
        <div class='flex min-h-56 min-w-0 flex-col bg-background'>
            <div class='flex shrink-0 items-start justify-between gap-3 py-2 pl-3'>
                <div class='flex min-w-0 flex-col items-start gap-1'>
                    <Heading level={1} class='block min-w-0 truncate text-2xl tracking-tight'>
                        {props.account.label}
                    </Heading>
                    <FundPicker symbol='' placeholder='Add a holding' icon={<PlusIcon />} class='-ml-1.5' onSelect={(symbol) => addPosition(props.account.kind, symbol)} />
                </div>
                <div class='flex shrink-0 flex-col items-end whitespace-nowrap pr-3'>
                    <Text weight='medium' class='text-xl tracking-tight'>
                        {formatCurrency(accountValue(props.account))}
                    </Text>
                    <span class='flex items-baseline gap-1'>
                        <Text tone={netPerMonth() == null ? undefined : netPerMonth()! >= 0 ? 'green' : 'red'}>{formatCurrency(netPerMonth())}</Text>
                        <Text tone='muted'>/ mo net</Text>
                    </span>
                </div>
            </div>

            <div class='min-h-0 flex-1 border-t'>
                <Show
                    when={held().length > 0}
                    fallback={
                        <Text tone='muted' class='block px-3 py-3'>
                            No holdings yet
                        </Text>
                    }>
                    <For each={held()}>{(holding) => <PositionRow account={props.account} holding={holding} />}</For>
                </Show>
            </div>

        </div>
    );
};

const Sandbox: Component = () => (
    <Panel>
        <Toolbar>
            <ToolbarSection>
                <Heading level={2} class='pl-2'>
                    Sandbox
                </Heading>
                <Text tone='muted' class='pl-2'>
                    nothing here is saved
                </Text>
            </ToolbarSection>
            <ToolbarSection>
                <Button size='fill' label='Copy from Main' title='Replace the sandbox with the holdings in your real book' disabled={!canCopyFromMain()} onClick={copyFromMain} />
                <Button size='fill' label='Clear' title='Empty the sandbox' disabled={isEmpty()} onClick={reset} />
            </ToolbarSection>
        </Toolbar>

        {/* The same three cards the real book leads with, from the same
            component — see cards.tsx. A sandbox whose headline figures were
            computed a second way would eventually disagree with the book for the
            same holdings, which would make it useless for the one thing it is
            for: deciding whether to actually do this. */}
        <div class='shrink-0'>
            <Cards
                figures={{
                    incomePerYear: totalIncome(),
                    netPerYear: netIncome(),
                    yieldPct: portfolioYield(),
                    value: totalValue(),
                    // No cost base worth stating — every position here was bought
                    // at today's price, so it would just restate the value. What
                    // is owed against it is the fact that is not already on screen.
                    valueNote: `${formatCurrency(equity())} equity after ${formatCurrency(totalDebt())} debt`,
                }}
            />
        </div>

        <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
            {/* Two by two rather than four stacked. Four full-width bands made
                the page a column of near-identical headers you had to read the
                label of to tell apart; side by side they are four places, and
                comparing what is in the TFSA against what is in the margin
                account stops being a scroll.

                Rows size to their contents and the cards stretch, so a pair
                shares a height and their footers line up. */}
            <div class='grid grid-cols-2 gap-px border-b bg-border'>
                <For each={accounts.filter(isShown)}>{(account) => <AccountCard account={account} />}</For>

                {/* The rest share a slot rather than taking a card each, same as
                    the real Accounts page. A sandbox opens empty — four blank
                    cards is a screen spent saying nothing, and the point of the
                    page is what you put in it. */}
                <Show when={accounts.some((a) => !isShown(a))}>
                    <div class='flex min-h-56 min-w-0 flex-col items-start gap-1 bg-background p-3'>
                        <Text tone='muted'>Not used yet</Text>
                        <For each={accounts.filter((a) => !isShown(a))}>
                            {(account) => <Button size='default' icon={<PlusIcon />} label={account.label} title={`Add ${account.label} to the sandbox`} onClick={() => activateAccount(account.kind)} />}
                        </For>
                    </div>
                </Show>
            </div>

            <div class='flex h-row items-center border-b bg-muted px-2'>
                <Text weight='medium'>Loans</Text>
            </div>

            {/* The Loans page's own four, from the same component — spread
                first, because it is the only one of them that is a verdict. The
                sandbox computes them from typed amounts and the book from a
                blotter, and sharing the row is what keeps the two comparable. */}
            <LoanCards
                figures={{
                    carryPerMonth: carryPerMonth(),
                    spread: spread(),
                    // The yield on the borrowed money, not on the book — the
                    // card sits over the loans and is about them.
                    yieldPct: borrowedYield(),
                    interestPerYear: totalInterest(),
                    borrowRate: borrowRate(),
                    debt: totalDebt(),
                    leverage: leverage(),
                    deductiblePerYear: deductibleInterest(),
                    dividendsPerYear: borrowedIncome(),
                }}
            />
            <div class='grid grid-cols-2 gap-px border-b bg-border'>
                <For each={loans}>
                    {(loan) => (
                        <div class='flex min-h-56 min-w-0 flex-col bg-background'>
                            <div class='flex shrink-0 items-start justify-between gap-3 py-2 pl-3'>
                                <div class='flex min-w-0 flex-col items-start gap-1'>
                                    <Heading level={1} class='block min-w-0 truncate text-2xl tracking-tight'>
                                        {loan.label}
                                    </Heading>
                                    <FundPicker
                                        symbol=''
                                        placeholder='Add a holding'
                                        icon={<PlusIcon />}
                                        class='-ml-1.5'
                                        onSelect={(symbol) => addPosition(defaultAccountFor(), symbol, loan.id)}
                                    />
                                </div>
                                <div class='flex shrink-0 flex-col items-end gap-1 whitespace-nowrap'>
                                    <NumberBox value={loan.balance} onValue={(n) => setLoanBalance(loan.id, n)} prefix='$' width='w-32 shrink-0' border='l' />
                                    <div class='flex items-center gap-2'>
                                        <NumberBox value={loan.rate} onValue={(n) => setLoanRate(loan.id, n)} suffix='%' width='w-20 shrink-0' border='l' />
                                        <span class='flex items-baseline gap-1 pr-3'>
                                            <Text tone={carry(loan.id) == null ? undefined : carry(loan.id)! >= 0 ? 'green' : 'red'}>{formatCurrency(carry(loan.id) == null ? null : carry(loan.id)! / 12)}</Text>
                                            <Text tone='muted'>/ mo</Text>
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div class='min-h-0 flex-1 border-t'>
                                <Show
                                    when={loanHoldings(loan.id).length > 0}
                                    fallback={
                                        <Text tone='muted' class='block px-3 py-3'>
                                            Nothing bought with this yet
                                        </Text>
                                    }>
                                    <For each={loanHoldings(loan.id)}>{(row) => <PositionRow account={row.account} holding={row.holding} showAccount />}</For>
                                </Show>
                            </div>

                            <Show when={loanIntoSheltered(loan.id) > 0.5}>
                                <div class='flex h-row shrink-0 items-center border-t px-3 whitespace-nowrap'>
                                    <Text tone='red' class='truncate'>{formatCurrency(loanIntoSheltered(loan.id))} not deductible</Text>
                                </div>
                            </Show>
                        </div>
                    )}
                </For>
            </div>
        </div>

        {/* What it comes to. Net income is the one that matters on a levered
            plan: distributions less the interest on the money that bought them. */}
        <div class='flex h-row shrink-0 items-center justify-between gap-4 border-t px-2 whitespace-nowrap'>
            <Text tone='muted'>
                {formatNumber(accounts.reduce((n, a) => n + a.holdings.length, 0))} positions · {formatCurrency(totalValue())} value · {formatCurrency(totalDebt())} debt · {formatCurrency(equity())} equity
            </Text>
            <span class='flex items-baseline gap-3'>
                <Text tone='muted'>Income</Text>
                <Text tone='green'>{formatCurrency(totalIncome())}</Text>
                <Text tone='muted'>less interest</Text>
                <Text tone='red'>{formatCurrency(totalInterest())}</Text>
                <Text tone='muted'>=</Text>
                <Text weight='medium' tone={netIncome() == null ? undefined : netIncome()! >= 0 ? 'green' : 'red'}>
                    {formatCurrency(netIncome())}
                </Text>
                <Text tone='muted'>/ yr</Text>
            </span>
        </div>
    </Panel>
);

export default Sandbox;
